import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";

import { Checkbox, FormGroup, Input, Textarea } from "@cofano/cck";

import NumberInput from "components/cck/NumberInput";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import LocationSelect from "view/locations/components/selects/LocationSelect";
import { LocationSchemaInput, LocationSchemaOutput } from "view/locations/utils/location.schema";
import { useLocationFieldPermission	} from "view/locations/utils/LocationUtils";

import { Fields, hasOptionalFieldPermission, Language } from "imports/Utils";

import LocationTypeSelect from "../selects/LocationTypeSelect";

import { LocationType } from "__generated__/enums";

const LocationGeneralForm = () => {

	const session = useStoreState(store => store.session);

	const { control } = useFormContext<LocationSchemaInput, unknown, LocationSchemaOutput>();
	const type = useWatch({ control, name: "type" });

	const fieldPermission = useLocationFieldPermission(type ?? null);

	return (
		<React.Fragment>
			<FormGroup
				label={Language("DESCRIPTION")}
				labelPlacement="left-center"
				overflow="hidden"
				required>
				<Controller
					name="description"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
				<Controller
					control={control}
					name="type"
					render={({ field }) => <LocationTypeSelect {...field} clearable={false} /> } />
			</FormGroup>

			<FormGroup label={Language("LOCATION_GROUP")} labelPlacement="left-center" overflow="hidden">
				<Controller
					control={control}
					name="locationGroups"
					render={({ field, formState }) => (
						<React.Fragment>
							<LocationSelect
								{...field}
								types={[ LocationType.Port, LocationType.Area, LocationType.NotificationArea ]}
								multiple />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.LOCATION, "externalId") && (
				<FormGroup label={Language("EXTERNAL_ID")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="externalId"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			<FormGroup label={Language("CODE")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="code"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup label={Language("ABBREVIATION")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="abbreviation"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup label={Language("EMAIL")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="email"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			<FormGroup label={Language("TELEPHONE")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="telephone"
					control={control}
					render={({ field, formState }) => (
						<React.Fragment>
							<Input {...field} />
							<FormErrorMessage name={field.name} errors={formState.errors} />
						</React.Fragment>
					)} />
			</FormGroup>

			{fieldPermission.hasPreHandlingTimeMinPermission && (
				<FormGroup label={Language("LOCATION_PRE_HANDLING_TIME_MIN")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="preHandlingTimeMin"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} addonAfter="min" decimalScale={0} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{fieldPermission.hasPostHandlingTimeMinPermission && (
				<FormGroup label={Language("LOCATION_POST_HANDLING_TIME_MIN")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="postHandlingTimeMin"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} addonAfter="min" decimalScale={0} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{fieldPermission.hasLoadingDepthPermission && (
				<FormGroup label={Language("LOCATION_LOADING_DEPTH")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="loadingDepth"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<NumberInput {...field} addonAfter="m" />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{fieldPermission.hasMaxShipLengthPermission && (
				<FormGroup label={Language("MAX_SHIP_LENGTH")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="maxShipLength"
						control={control}
						render={({ field }) => <NumberInput {...field} addonAfter="m" />} />
				</FormGroup>
			)}

			{fieldPermission.hasMaxShipWidthPermission && (
				<FormGroup label={Language("MAX_SHIP_WIDTH")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="maxShipWidth"
						control={control}
						render={({ field }) => <NumberInput {...field} addonAfter="m" />} />
				</FormGroup>
			)}

			{fieldPermission.hasMaxShipDraughtPermission && (
				<FormGroup label={Language("MAX_SHIP_DRAUGHT")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="maxShipDraught"
						control={control}
						render={({ field }) => <NumberInput {...field} addonAfter="m" />} />
				</FormGroup>
			)}

			<FormGroup label={intl.formatMessage({ id: "LOCATION_NOTES" })} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="notes"
					control={control}
					render={({ field }) => (
						<Textarea {...field} />
					)} />
			</FormGroup>

			<FormGroup label={Language("COLOR")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="color"
					control={control}
					render={({ field }) => <Input {...field} type="color" />} />
			</FormGroup>

			<FormGroup label={Language("ACTIVE")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="isActive"
					control={control}
					render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.LOCATION, "erpLocation") && (
				<FormGroup label={Language("ERP_LOCATION")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="isErpLocation"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}

			{type === LocationType.InlandTerminal && (
				<FormGroup label={Language("UAB_REGISTRATION")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="uabRegistration"
						control={control}
						render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
				</FormGroup>
			)}
		</React.Fragment>
	);
};

export default LocationGeneralForm;
